import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { CheckSquare, Filter } from 'lucide-react';

const STATUS_LABELS = { pending: 'Pendiente', in_progress: 'En Progreso', review: 'Revisión', completed: 'Completada' };
const STATUS_COLORS = { pending: '#D4A574', in_progress: '#C9A66B', review: '#8B9F8D', completed: '#6B8F6B' };
const PRIORITY_COLORS = { high: '#B87B7B', medium: '#D4A574', low: '#8B9F8D' };

export default function OSTasks() {
  const { authFetch } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');

  const load = async () => {
    try {
      const [tRes, pRes] = await Promise.all([authFetch('/api/tasks/'), authFetch('/api/projects/')]);
      setTasks(await tRes.json());
      setProjects(await pRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (taskId, status) => {
    try {
      await authFetch(`/api/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      load();
    } catch { /* */ }
  };

  const projectMap = {};
  projects.forEach(p => { projectMap[p.id] = p.name; });

  const filtered = tasks.filter(t => {
    if (filter !== 'all' && t.status !== filter) return false;
    if (projectFilter !== 'all' && t.project_id !== projectFilter) return false;
    return true;
  });

  // Group by status for kanban-like view
  const grouped = { pending: [], in_progress: [], review: [], completed: [] };
  filtered.forEach(t => { if (grouped[t.status]) grouped[t.status].push(t); });

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#C9A66B', borderTopColor: 'transparent' }} /></div>;

  return (
    <div data-testid="os-tasks-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>Tareas</h1>
          <p className="text-sm mt-1" style={{ color: '#C9B8A8' }}>{tasks.length} tarea{tasks.length !== 1 ? 's' : ''} total</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="task-filter-status" value={filter} onChange={e => setFilter(e.target.value)}
            className="px-3 py-2 rounded-lg text-xs outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8E3D8', color: '#2D2D2D' }}>
            <option value="all">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select data-testid="task-filter-project" value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
            className="px-3 py-2 rounded-lg text-xs outline-none" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8E3D8', color: '#2D2D2D' }}>
            <option value="all">Todos los proyectos</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* Kanban columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {Object.entries(grouped).map(([status, items]) => (
          <div key={status} className="rounded-xl border p-4" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[status] }} />
              <span className="text-xs font-medium tracking-wide" style={{ color: STATUS_COLORS[status] }}>{STATUS_LABELS[status]}</span>
              <span className="text-[10px] ml-auto px-1.5 py-0.5 rounded" style={{ backgroundColor: '#FAF9F6', color: '#C9B8A8' }}>{items.length}</span>
            </div>
            <div className="space-y-2">
              {items.map(t => (
                <div key={t.id} data-testid={`kanban-task-${t.id}`} className="rounded-lg border p-3 transition-all hover:shadow-sm" style={{ borderColor: '#E8E3D8' }}>
                  <div className="flex items-start gap-2 mb-2">
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: PRIORITY_COLORS[t.priority] }} />
                    <p className="text-sm font-light leading-snug" style={{ color: '#2D2D2D' }}>{t.title}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px]" style={{ color: '#C9B8A8' }}>{projectMap[t.project_id] || ''}</span>
                    <select
                      value={t.status}
                      onChange={e => updateStatus(t.id, e.target.value)}
                      className="text-[10px] rounded px-1 py-0.5 outline-none cursor-pointer"
                      style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }}
                    >
                      {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
              ))}
              {items.length === 0 && (
                <p className="text-xs text-center py-4 font-light" style={{ color: '#C9B8A8' }}>Sin tareas</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
